<template>
  <div class="guide-evidence">
    <h2>What is the evidence system?</h2>
    <p>
      A comprehensive audit and compliance framework that proves who signed what,
      when, how they were authenticated, and that nothing was tampered with after
      signing. This is what makes electronic signatures legally defensible.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Why evidence matters</h2>
    <ul>
      <li>
        <strong>Legal defensibility.</strong> In court, you need to prove the
        signer's identity, intent, and the document's integrity. The evidence
        system provides all three.
      </li>
      <li>
        <strong>Regulatory compliance.</strong> Industries like real estate,
        finance, and healthcare have specific e-signature requirements. A complete
        evidence trail demonstrates compliance with ESIGN, UETA, and
        industry-specific regulations.
      </li>
      <li>
        <strong>Dispute resolution.</strong> When a signer claims "I didn't sign
        that," the evidence pack proves otherwise, with timestamps, IP addresses,
        authentication records, and cryptographic integrity verification.
      </li>
      <li>
        <strong>Audit requirements.</strong> Many organizations need immutable
        records of all signing activities. The evidence system provides append-only
        logs that cannot be modified or deleted after creation.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Three layers of evidence</h2>
    <p>
      The evidence system is built on three complementary layers. Each layer
      reinforces the others to create a complete proof chain.
    </p>

    <h3>Audit trail</h3>
    <p>
      An immutable event log recording every action taken on a package. The audit
      trail is the foundation of the evidence system.
    </p>
    <ul>
      <li>
        Over 40 event types cover the full lifecycle: PackageCreated,
        DocumentUploaded, SignerAdded, NotificationSent, SignerViewed,
        AnnotationCompleted, SignerCompleted, PackageCompleted, PackageVoided, and
        many more.
      </li>
      <li>
        Each event records a UTC timestamp, the actor (email, IP address, user
        agent), and event-specific details.
      </li>
      <li>
        Events are append-only. They cannot be modified or deleted after
        creation. This guarantees the integrity of the historical record.
      </li>
    </ul>

    <h3>Certificate of Completion</h3>
    <p>
      An auto-generated PDF summarizing the entire signing transaction. The
      certificate serves as a human-readable evidence document suitable for legal
      proceedings and compliance audits.
    </p>
    <ul>
      <li>Lists all signers with completion timestamps and signing order.</li>
      <li>Includes the authentication method used by each signer.</li>
      <li>Embeds the audit-trail summary.</li>
      <li>Platform-signed for integrity verification.</li>
    </ul>

    <h3>Hash-chain integrity</h3>
    <p>
      Cryptographic proof that no events were tampered with. Each audit event
      includes a SHA-256 hash of itself plus the previous event's hash, creating
      an unbroken chain. Modifying any single event breaks the chain, making
      tampering immediately detectable.
    </p>
    <GuideCodeTabs :samples="samples.verifyIntegrity" />

    <p>
      The verification response confirms chain integrity across both storage layers:
    </p>

    <GuideCodeTabs :samples="samples.verifyResponse" />
    <table class="q-table q-mb-md" style="width: 100%; border-collapse: collapse;">
      <thead>
        <tr>
          <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Field</th>
          <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Meaning</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;"><code>isValid</code></td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Overall pass/fail (all three sub-checks must succeed)</td>
        </tr>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;"><code>sqlChainValid</code></td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">All event hashes in the database form an unbroken chain</td>
        </tr>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;"><code>blobChainValid</code></td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">All event hashes in immutable blob storage form an unbroken chain</td>
        </tr>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;"><code>sqlBlobMatch</code></td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Database and blob storage chains match (detects database tampering)</td>
        </tr>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;"><code>errorMessage</code></td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Null on success; describes which check failed when <code>isValid</code> is false</td>
        </tr>
      </tbody>
    </table>

    <q-separator class="q-my-lg" />

    <h2>Viewing evidence in the portal</h2>
    <p>
      Every completed package's detail page has an <strong>Audit Trail</strong>
      tab that shows the full event log: who opened the signing link, when they
      authenticated, when each field was completed, and when the package was
      finalized. Each event includes the actor's IP, user agent, and timestamp.
    </p>
    <p>
      From the same page, download buttons give you the combined signed PDF (all
      documents with signatures and stamps burned in) and the
      <strong>Certificate of Completion</strong>, a human-readable audit summary
      suitable for legal records. The audit trail can also be exported as JSON,
      CSV, or PDF for archival or external review.
    </p>

    <section class="dev-section">
    <h2>For developers and enterprise integration</h2>
    <p>
      All evidence surfaces are available programmatically: audit-trail
      retrieval, format exports (with optional IP redaction for privacy),
      certificate download URL, combined signed PDF URL, and hash-chain integrity
      verification. All reads sit under the <code>sign.package.read</code> scope.
      Most are rate-limited under the <code>export</code> policy at 5 requests
      per minute.
    </p>

    <h3>Accessing evidence</h3>

    <h3>Audit trail</h3>
    <p>Retrieve the complete event log for a package as structured JSON.</p>
    <GuideCodeTabs :samples="samples.auditTrail" />

    <h3>Export</h3>
    <p>
      Export the audit trail in your preferred format for archival or legal
      proceedings.
    </p>

    <GuideCodeTabs :samples="samples.auditExport" />
    <p>
      All three export formats and the integrity-verification endpoint share the
      <code>export</code> rate-limit policy at 5 requests per minute per tenant.
      When redacted, IP addresses are replaced with <code>[REDACTED]</code>. The
      rest of the audit record remains intact.
    </p>

    <h3>Certificate of Completion</h3>
    <p>Download the auto-generated Certificate of Completion PDF.</p>
    <GuideCodeTabs :samples="samples.certificate" />
    <p>
      Returns <code>{ url, expiresOn }</code>, a time-limited pre-signed URL for
      the certificate PDF. The certificate is available only for packages in
      Completed status and is generated on demand. The first call takes slightly
      longer. Subsequent calls return a cached version. Also rate-limited under
      the <code>export</code> policy at 5 per minute.
    </p>

    <h3>Signed documents</h3>
    <p>
      Download all signed documents as a single combined PDF. This is the legally
      executed artifact: one file containing every document in the package, with
      all signatures, initials, text fields, and stamps permanently burned into
      the pages.
    </p>
    <GuideCodeTabs :samples="samples.packageDownload" />
    <p>
      Returns <code>{ url, expiresOn }</code>, a pre-signed URL for the combined
      PDF. To fetch a specific document alone, use the document-level
      <code>/download-signed</code> endpoint instead. See the Documents guide.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Integrity verification</h3>
    <p>
      The evidence system uses a dual-storage verification model to ensure
      tamper-proof records.
    </p>
    <ul>
      <li>
        <strong>SQL chain.</strong> Events stored in the database with linked
        hashes. Each event's hash includes the previous event's hash, forming a
        sequential chain.
      </li>
      <li>
        <strong>Blob chain.</strong> Events archived to immutable blob storage
        with linked hashes. Immutable storage prevents modification or deletion
        at the infrastructure level.
      </li>
      <li>
        <strong>Cross-check.</strong> The SQL and blob chains must match. A
        mismatch between the two indicates that the database has been tampered
        with after the events were archived.
      </li>
      <li>
        <strong>Regular verification.</strong> Run the verification endpoint
        after completing sensitive transactions to confirm no post-signing
        modifications have occurred.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h3>Best practices</h3>
    <ul>
      <li>
        <strong>Download and archive evidence packs for completed packages.</strong>
        Do not rely solely on the platform for long-term storage. Maintain your
        own copies of the signed documents, certificate of completion, and
        audit-trail exports.
      </li>
      <li>
        <strong>Use hash-chain verification after completing sensitive
        transactions.</strong> A quick verification call confirms the entire
        event history is intact and has not been altered.
      </li>
      <li>
        <strong>Export audit trails in PDF format for legal proceedings.</strong>
        The PDF export is human-readable and self-contained, which makes it
        suitable for submission as evidence without requiring technical
        interpretation.
      </li>
      <li>
        <strong>Enable IP redaction when sharing audit exports with external
        parties.</strong> Use the <code>redactIp=true</code> parameter to mask
        signer IP addresses when sharing exports with parties who should not
        have access to that information.
      </li>
      <li>
        <strong>Combine strong authentication with the evidence pack for
        highest assurance.</strong> Pairing KBA or ID Verification with the
        evidence system creates a comprehensive identity and intent proof chain
        that is difficult to dispute.
      </li>
    </ul>

    <q-banner class="bg-blue-1 text-blue-9 q-mt-lg" rounded>
      <strong>Tip:</strong> For maximum legal defensibility, use the evidence
      trifecta. Require strong signer authentication (SMS OTP plus KBA), verify
      the hash chain after completion, and archive the full evidence pack
      (signed documents, certificate, and audit-trail export) in your own
      document management system.
    </q-banner>

    <q-banner class="bg-green-1 text-green-9 q-mt-md" rounded>
      <template v-slot:avatar>
        <q-icon name="menu_book" color="green-9" />
      </template>
      See the <strong>Packages</strong> section in the
      <a href="/Docs/ApiReference" class="text-green-9" style="text-decoration: underline;">API Reference</a>
      for complete endpoint schemas and try-it-out examples.
    </q-banner>
    </section>
  </div>
</template>

<script>
import GuideCodeTabs from './shared/GuideCodeTabs.vue';

// Helper pattern used by the three "GET a pre-signed URL" endpoints. The shape is identical
// across verify / audit-trail / certificate / download - only the path segment changes.
function getUrlSamples(pathSegment, commentLine) {
    return {
        curl: `# ${commentLine}
curl https://api.esignlaunchpad.com/api/v1/packages/$PACKAGE_ID${pathSegment} \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const response = await fetch(
  \`https://api.esignlaunchpad.com/api/v1/packages/\${packageId}${pathSegment}\`,
  { headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } }
);
const result = await response.json();`,

        node: `const response = await fetch(
  \`https://api.esignlaunchpad.com/api/v1/packages/\${packageId}${pathSegment}\`,
  { headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } }
);
if (!response.ok) throw new Error(\`\${response.status} \${await response.text()}\`);
const result = await response.json();`,

        python: `response = requests.get(
    f'https://api.esignlaunchpad.com/api/v1/packages/{package_id}${pathSegment}',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
)
response.raise_for_status()
result = response.json()`,

        csharp: `var result = await client.GetFromJsonAsync<JsonElement>(
    $"api/v1/packages/{packageId}${pathSegment}");`,

        php: `$response = $client->get("api/v1/packages/{$packageId}${pathSegment}", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
]);
$result = json_decode($response->getBody(), true);`,

        ruby: `uri = URI("https://api.esignlaunchpad.com/api/v1/packages/#{package_id}${pathSegment}")
req = Net::HTTP::Get.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
result = JSON.parse(res.body)`
    };
}

const samples = {
    // ─── Verify hash-chain integrity (single event endpoint call) ───────────────
    verifyIntegrity: getUrlSamples('/audit-trail/verify',
        'Rate-limited: 5 req/min under the `export` policy'),

    // ─── Response body from /audit-trail/verify ─────────────────────────────────
    verifyResponse: {
        json: `{
  "isValid": true,
  "sqlChainValid": true,
  "blobChainValid": true,
  "sqlBlobMatch": true,
  "errorMessage": null
}`
    },

    // ─── Retrieve the full audit-trail event list ───────────────────────────────
    auditTrail: getUrlSamples('/audit-trail', 'Read the immutable event list for a package'),

    // ─── Audit export (json / csv / pdf, with optional IP redaction) ────────────
    auditExport: {
        curl: `# JSON
curl "https://api.esignlaunchpad.com/api/v1/packages/$PACKAGE_ID/audit-trail/export?format=json" \\
  -H "Authorization: Bearer $ESIGN_TOKEN"

# CSV
curl "https://api.esignlaunchpad.com/api/v1/packages/$PACKAGE_ID/audit-trail/export?format=csv" \\
  -H "Authorization: Bearer $ESIGN_TOKEN"

# PDF (human-readable, self-contained)
curl "https://api.esignlaunchpad.com/api/v1/packages/$PACKAGE_ID/audit-trail/export?format=pdf" \\
  -H "Authorization: Bearer $ESIGN_TOKEN" \\
  -o audit-trail.pdf

# PDF with IP addresses redacted (privacy compliance)
curl "https://api.esignlaunchpad.com/api/v1/packages/$PACKAGE_ID/audit-trail/export?format=pdf&redactIp=true" \\
  -H "Authorization: Bearer $ESIGN_TOKEN" \\
  -o audit-trail-redacted.pdf`,

        js: `const url = new URL(
  \`https://api.esignlaunchpad.com/api/v1/packages/\${packageId}/audit-trail/export\`
);
url.searchParams.set('format', 'pdf');      // json | csv | pdf
url.searchParams.set('redactIp', 'true');   // optional, privacy compliance

const response = await fetch(url, { headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } });
const blob = await response.blob();
// Hand the blob off to the user's browser as a download...`,

        node: `const fs = require('node:fs');
const { pipeline } = require('node:stream/promises');

const url = new URL(
  \`https://api.esignlaunchpad.com/api/v1/packages/\${packageId}/audit-trail/export\`
);
url.searchParams.set('format', 'pdf');
url.searchParams.set('redactIp', 'true');

const response = await fetch(url, { headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } });
await pipeline(response.body, fs.createWriteStream('audit-trail.pdf'));`,

        python: `import os, requests

params = {'format': 'pdf', 'redactIp': 'true'}
url = f'https://api.esignlaunchpad.com/api/v1/packages/{package_id}/audit-trail/export'

with requests.get(url, headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
                  params=params, stream=True) as r:
    r.raise_for_status()
    with open('audit-trail.pdf', 'wb') as f:
        for chunk in r.iter_content(8192):
            f.write(chunk)`,

        csharp: `var response = await client.GetAsync(
    $"api/v1/packages/{packageId}/audit-trail/export?format=pdf&redactIp=true");
response.EnsureSuccessStatusCode();
await using var file = File.Create("audit-trail.pdf");
await response.Content.CopyToAsync(file);`,

        php: `$response = $client->get("api/v1/packages/{$packageId}/audit-trail/export", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'query' => ['format' => 'pdf', 'redactIp' => 'true'],
    'sink' => 'audit-trail.pdf',
]);`,

        ruby: `uri = URI("https://api.esignlaunchpad.com/api/v1/packages/#{package_id}/audit-trail/export")
uri.query = URI.encode_www_form(format: 'pdf', redactIp: 'true')
req = Net::HTTP::Get.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) do |http|
  File.open('audit-trail.pdf', 'wb') do |f|
    http.request(req) { |res| res.read_body { |chunk| f.write(chunk) } }
  end
end`
    },

    // ─── Certificate of Completion URL (Completed status only) ──────────────────
    certificate: getUrlSamples('/certificate',
        'Returns { url, expiresOn }. Available only when status is Completed. Rate-limited 5/min.'),

    // ─── Combined signed-PDF package download URL ───────────────────────────────
    packageDownload: getUrlSamples('/download',
        'Returns { url, expiresOn } - pre-signed URL for the combined signed PDF')
};

export default {
    name: 'GuideEvidence',
    components: { GuideCodeTabs },
    data() { return { samples }; }
};
</script>
